import { WidgetTemplate } from './types';
import { dashboardPalette } from '../../utils/colors';

export const WIDGET_TEMPLATES: WidgetTemplate[] = [
  {
    type: 'bar',
    label: 'Bar Chart',
    description: 'Horizontal or vertical bar chart for comparing categories',
    defaultConfig: {
      colors: [dashboardPalette[0], dashboardPalette[1], dashboardPalette[2]],
      labels: { x: 'Date', y: 'Messages count' },
      legend: { position: 'none' },
      stacked: true,
      showGrid: true,
      barDirection: "vertical",
      columnMapping: { "key": "id", "label": "label", "xAxis": "date", "yAxis": "number_of_publications"},
      echartsOption: {}
    },
    sampleQuery: "SELECT\n" +
      "    n.id AS id,\n" +
      "    n.name AS label,\n" +
      "    (stat->>'date_publicated')::DATE AS date,\n" +
      "    (stat->>'publications')::INT AS number_of_publications\n" +
      "FROM narrative n\n" +
      "JOIN narrative_evolution_chart_widget necw ON necw.narrative_id = n.id\n" +
      "CROSS JOIN jsonb_array_elements(necw.objects) obj\n" +
      "CROSS JOIN jsonb_array_elements(obj->'stats') stat\n" +
      "WHERE\n" +
      "    n.id = ANY({{case_ids}}) AND\n" +
      "    necw.breakdown = 'NONE' AND\n" +
      "    necw.aggregation = 'DAY' AND\n" +
      "    (stat->>'date_publicated')::DATE >= NOW() - INTERVAL '30 DAY'\n" +
      "ORDER BY date, n.id;",
  },
  {
    type: 'line',
    label: 'Line Chart',
    description: 'Line chart for showing trends over time',
    defaultConfig: {
      colors: [dashboardPalette[0], dashboardPalette[1], dashboardPalette[2]],
      labels: { x: 'Date', y: 'Messages count' },
      legend: { position: 'bottom' },
      showGrid: true,
      columnMapping: { "key": "id", "label": "label", "xAxis": "date", "yAxis": "number_of_publications"},
      echartsOption: {},
      clickAction: {
        type: "url",
        url: "https://app.osavul.cloud/narratives/{id}"
      }
    },
    sampleQuery: "SELECT\n" +
      "    n.id AS id,\n" +
      "    n.name AS label,\n" +
      "    (stat->>'date_publicated')::DATE AS date,\n" +
      "    (stat->>'publications')::INT AS number_of_publications\n" +
      "FROM narrative n\n" +
      "JOIN narrative_evolution_chart_widget necw ON necw.narrative_id = n.id\n" +
      "CROSS JOIN jsonb_array_elements(necw.objects) obj\n" +
      "CROSS JOIN jsonb_array_elements(obj->'stats') stat\n" +
      "WHERE\n" +
      "    n.id = ANY({{case_ids}}) AND\n" +
      "    necw.breakdown = 'NONE' AND\n" +
      "    necw.aggregation = 'DAY' AND\n" +
      "    (stat->>'date_publicated')::DATE >= NOW() - INTERVAL '30 DAY'\n" +
      "ORDER BY date, n.id;",
  },
  {
    type: 'pie',
    label: 'Pie Chart',
    description: 'Pie or donut chart for showing proportions',
    defaultConfig: {
      colors: dashboardPalette.slice(0, 6),
      legend: { position: 'none' },
      pieHole: 0.5,
      columnMapping: { name: 'label', value: 'total_messages' },
      echartsOption: {},
    },
    sampleQuery: "SELECT\n" +
      "    n.name AS label,\n" +
      "    n.total_messages AS total_messages\n" +
      "FROM narrative n\n" +
      "WHERE\n" +
      "    n.id = ANY({{case_ids}});",
  },
  {
    type: 'table',
    label: 'Data Table',
    description: 'Tabular view of raw query results',
    defaultConfig: {},
    sampleQuery: "SELECT\n" +
      "    n.*\n" +
      "FROM narrative n\n" +
      "WHERE\n" +
      "    n.id = ANY({{case_ids}});",
  },
  {
    type: 'metric',
    label: 'Single Value Card',
    description: 'A prominent single metric with optional sparkline and trend indicator',
    defaultConfig: {
      metricConfig: {
        trendRange: 30,
        mainValueMode: 'last',
      },
      columnMapping: {},
    },
    sampleQuery: "SELECT\n" +
      "    (stat->>'date_publicated')::DATE AS date,\n" +
      "    SUM((stat->>'publications')::INT) AS number_of_publications\n" +
      "FROM narrative n\n" +
      "JOIN narrative_evolution_chart_widget necw ON necw.narrative_id = n.id\n" +
      "CROSS JOIN jsonb_array_elements(necw.objects) obj\n" +
      "CROSS JOIN jsonb_array_elements(obj->'stats') stat\n" +
      "WHERE\n" +
      "    n.id = ANY({{case_ids}}) AND\n" +
      "    necw.breakdown = 'NONE' AND\n" +
      "    necw.aggregation = 'DAY' AND\n" +
      "    (stat->>'date_publicated')::DATE >= NOW() - INTERVAL '30 DAY'\n" +
      "GROUP BY date\n" +
      "ORDER BY date;",
  },
  {
    type: 'card_grid',
    label: 'Card Grid',
    description: 'A responsive grid of cards — date, title, sparkline, description, and metrics',
    defaultConfig: {
      "cardGridConfig": {
        "dateCol": "first_date",
        "dateEndCol": "last_date",
        "titleCol": "title",
        "sparklineCol": "sparkline_data",
        "sparklineLabel": "Daily publications",
        "clickUrlTemplate": "/topic_reports/{narrative_id}/events/{id}",
        "clickTarget": "_blank",
        "metrics": [
          {
            "label": "Publications",
            "col": "publications",
            "format": "compact"
          },
          {
            "label": "Views",
            "col": "views",
            "format": "compact"
          },
          {
            "label": "Engagement",
            "col": "engagement",
            "format": "compact"
          },
          {
            "label": "Sentiment",
            "col": "sentiment",
            "format": "compact"
          }
        ]
      }
    },
    sampleQuery: "SELECT\n" +
      "    e.id \"id\",\n" +
      "    e.narrative_id \"narrative_id\",\n" +
      "    e.first_message_date \"first_date\",\n" +
      "    e.last_message_date \"last_date\",\n" +
      "    e.title \"title\",\n" +
      "    ((e.summary_stats->>'publications')::JSONB->>'total')::INT \"publications\",\n" +
      "    ((e.summary_stats->>'reactions')::JSONB->>'total')::INT \"engagement\",\n" +
      "    ((e.summary_stats->>'views')::JSONB->>'total')::INT \"views\",\n" +
      "    (e.opinion_balance->>'sentiment') \"sentiment\",\n" +
      "    (\n" +
      "        SELECT json_agg(json_build_object('date', daily.d, 'cnt', daily.cnt) ORDER BY daily.d)::TEXT\n" +
      "        FROM (\n" +
      "            SELECT message_date_publicated::DATE AS d, COUNT(*) AS cnt\n" +
      "            FROM topic_modeling_event_messages\n" +
      "            WHERE event_id = e.id\n" +
      "            GROUP BY d\n" +
      "        ) daily\n" +
      "    ) AS \"sparkline_data\"\n" +
      "FROM topic_modeling_events e\n" +
      "WHERE e.narrative_id = ANY({{case_ids}})\n" +
      "ORDER BY \"publications\" DESC, \"engagement\" DESC, \"views\" DESC\n" +
      "LIMIT 9;",
  },
  {
    type: 'opinions',
    label: 'Opinions',
    description: 'A grid of opinion cards — sentiment icon, title, and rating/publications/actors metrics',
    defaultConfig: {
      "opinionsConfig": {
        "titleCol": "title",
        "sentimentCol": "sentiment",
        "clickUrlTemplate": "/topic_reports/{narrative_id}/events/{event_id}",
        "clickTarget": "_blank",
        "metrics": [
          {
            "label": "Rating",
            "col": "rating",
            "format": "compact",
            "tooltip": "Rating based on the number of reactions and comments"
          },
          {
            "label": "Publications",
            "col": "publications",
            "format": "compact"
          },
          {
            "label": "Actors",
            "col": "actors",
            "format": "compact"
          }
        ]
      }
    },
    sampleQuery: "SELECT\n" +
      "    o.id \"id\",\n" +
      "    o.narrative_id \"narrative_id\",\n" +
      "    o.event_id \"event_id\",\n" +
      "    o.title \"title\",\n" +
      "    LOWER(o.sentiment) \"sentiment\",\n" +
      "    ROUND(o.rating)::INT \"rating\",\n" +
      "    o.messages_count \"publications\",\n" +
      "    (\n" +
      "        SELECT COUNT(DISTINCT om.actor_id)\n" +
      "        FROM topic_modeling_opinion_messages om\n" +
      "        WHERE om.opinion_id = o.id AND om.is_deleted = false\n" +
      "    ) \"actors\"\n" +
      "FROM topic_modeling_opinions o\n" +
      "WHERE o.narrative_id = ANY({{case_ids}})\n" +
      "ORDER BY o.rating DESC NULLS LAST\n" +
      "LIMIT 9;",
  },
  {
    type: 'map',
    label: 'World Map',
    description: 'Choropleth world map — color countries by a numeric value, show details on hover',
    defaultConfig: {
      mapConfig: {
        countryColumn: 'country_code',
        valueColumn: 'publications',
        tooltipColumns: [
          'publications',
          'actors',
          'state_affiliated_actors',
          'inauthentic_actors',
          'high_risk_actors',
        ],
        tooltipLabels: {
          publications: 'Publications',
          actors: 'Actors',
          state_affiliated_actors: 'State-affiliated actors',
          inauthentic_actors: 'Inauthentic actors',
          high_risk_actors: 'High-risk actors',
        },
        colorRange: ['#dbeafe', '#1d4ed8'],
        emptyColor: '#e5e7eb',
        roam: true,
      },
    },
    sampleQuery:
      'SELECT\n' +
      '    c.code AS country_code,\n' +
      '    SUM(na.messages_count) AS publications,\n' +
      '    COUNT(DISTINCT na.actor_id) AS actors,\n' +
      '    COUNT(DISTINCT CASE WHEN sa.source_id IS NOT NULL THEN s.id END) AS state_affiliated_actors,\n' +
      '    COUNT(DISTINCT CASE WHEN s.is_bot IS TRUE THEN s.id END) AS inauthentic_actors,\n' +
      '    COUNT(DISTINCT CASE WHEN sd.source_id IS NOT NULL THEN s.id END) AS high_risk_actors\n' +
      'FROM narrative_actors na\n' +
      'JOIN source s ON s.id = na.actor_id\n' +
      'JOIN country c ON c.id = s.origin_country_id\n' +
      'LEFT JOIN (SELECT DISTINCT source_id FROM source_affiliation) sa ON sa.source_id = s.id\n' +
      'LEFT JOIN (SELECT DISTINCT source_id FROM source_discreditation) sd ON sd.source_id = s.id\n' +
      'WHERE na.narrative_id = ANY({{case_ids}})\n' +
      'GROUP BY c.code\n' +
      'ORDER BY 2 DESC;',
  },
  {
    type: 'word_cloud',
    label: 'Word / Emoji Cloud',
    description: 'Most frequent keywords and emojis in case messages — size shows how often each is mentioned, color shows what is rising or falling, or the tone it is mentioned in',
    defaultConfig: {
      columnMapping: { name: 'name', value: 'value' },
      wordCloudConfig: {
        mode: 'both',
        kindColumn: 'kind',
        trendColumn: 'trend',
        trendLabel: 'newer vs older half of the sample',
        sentimentColumn: 'sentiment',
        sampleColumn: 'sample_size',
        showModeToggle: true,
        maxWords: 100,
        minWordLength: 3,
        stopWords: [],
        shape: 'circle',
      },
    },
    sampleQuery:
      "-- Top 50 keywords + top 50 emojis from the newest ~1000 messages of the chosen cases.\n" +
      "-- The 1000-message budget is split equally between cases; cost grows linearly with it.\n" +
      "-- The messages of each case are cut into a newer and an older half to tell what is rising.\n" +
      "-- Sentiment is the message-level value stored on the message row itself.\n" +
      "WITH picked AS (\n" +
      "    SELECT s.message_id, min(s.half) AS half\n" +
      "    FROM unnest({{case_ids}}::integer[]) AS c(id)\n" +
      "    CROSS JOIN LATERAL (\n" +
      "        SELECT v.message_id, ntile(2) OVER (ORDER BY v.message_id DESC) AS half\n" +
      "        FROM (\n" +
      "            SELECT u.message_id\n" +
      "            FROM (\n" +
      "                (SELECT nm.message_id\n" +
      "                 FROM narrative_message nm\n" +
      "                 WHERE nm.narrative_id = c.id AND nm.status = 'NEW'\n" +
      "                 ORDER BY nm.message_id DESC\n" +
      "                 LIMIT GREATEST(1000 / NULLIF(cardinality({{case_ids}}::integer[]), 0), 5))\n" +
      "                UNION ALL\n" +
      "                (SELECT nm.message_id\n" +
      "                 FROM narrative_message nm\n" +
      "                 WHERE nm.narrative_id = c.id AND nm.status = 'READ'\n" +
      "                 ORDER BY nm.message_id DESC\n" +
      "                 LIMIT GREATEST(1000 / NULLIF(cardinality({{case_ids}}::integer[]), 0), 5))\n" +
      "            ) u\n" +
      "            ORDER BY u.message_id DESC\n" +
      "            LIMIT GREATEST(1000 / NULLIF(cardinality({{case_ids}}::integer[]), 0), 5)\n" +
      "        ) v\n" +
      "    ) s\n" +
      "    GROUP BY s.message_id\n" +
      "),\n" +
      "msgs AS MATERIALIZED (\n" +
      "    SELECT p.half, upper(m.sentiment) AS sentiment, left(m.text, 1500) AS txt\n" +
      "    FROM picked p\n" +
      "    JOIN message m ON m.id = p.message_id\n" +
      "),\n" +
      "sizes AS (\n" +
      "    SELECT count(*) AS total,\n" +
      "           count(*) FILTER (WHERE half = 1) + 1.0 AS newer,\n" +
      "           count(*) FILTER (WHERE half = 2) + 1.0 AS older\n" +
      "    FROM msgs\n" +
      "),\n" +
      "words AS (\n" +
      "    SELECT lower(t.token) AS name,\n" +
      "           count(*) AS value,\n" +
      "           count(*) FILTER (WHERE msgs.half = 1) AS recent,\n" +
      "           count(*) FILTER (WHERE msgs.sentiment = 'POSITIVE') AS pos,\n" +
      "           count(*) FILTER (WHERE msgs.sentiment = 'NEGATIVE') AS neg,\n" +
      "           count(msgs.sentiment) AS rated\n" +
      "    FROM msgs, LATERAL ts_parse('default', msgs.txt) AS t\n" +
      "    WHERE t.tokid IN (1, 2) AND char_length(t.token) >= 4\n" +
      "    GROUP BY 1\n" +
      "    ORDER BY 2 DESC\n" +
      "    LIMIT 300\n" +
      "),\n" +
      "top_words AS (\n" +
      "    SELECT name, value, recent, pos, neg, rated\n" +
      "    FROM words\n" +
      "    -- stop words: built-in English and Russian dictionaries, plus a manual Ukrainian list\n" +
      "    WHERE ts_lexize('english_stem', name) <> '{}'\n" +
      "      AND ts_lexize('russian_stem', name) <> '{}'\n" +
      "      AND name <> ALL (ARRAY[\n" +
      "        'його','їхні','їхній','тому','може','можуть','можна','чому','коли','куди','який','яка','яке','які','яких','якій','якого','якому','якщо','якби',\n" +
      "        'було','була','були','буде','будуть','бути','вони','вона','воно','вами','нами','мене','мені','тебе','тобі','себе','собі','йому','нього','ними',\n" +
      "        'також','теж','після','перед','проти','через','серед','біля','щодо','понад','близько','крім','саме','зараз','тепер','тоді','потім','знову','завжди','ніколи',\n" +
      "        'треба','потрібно','більше','менше','дуже','майже','просто','далі','лише','тільки','навіть','хоча','адже','тобто','отже','однак','проте','поки','щоб','щоби',\n" +
      "        'цього','цьому','цієї','цей','того','такий','така','таке','такі','таких','весь','всіх','всім','свої','своїх','свого','своєї','свою','наші','наша','ваші',\n" +
      "        'інші','інших','інший','інша','один','одна','одне','одного','кожен','кожна','щось','хтось','десь','нічого','немає','зокрема','разом','зовсім',\n" +
      "        'сьогодні','вчора','завтра','року','році','роки','років','має','мають','мати',\n" +
      "        'также','очень','который','которые','которая','которых','этих','своих','свои','будут','могут','именно','однако','пока','году','года',\n" +
      "        'http','https','www'])\n" +
      "    ORDER BY value DESC\n" +
      "    LIMIT 50\n" +
      "),\n" +
      "top_emojis AS (\n" +
      "    SELECT e[1] AS name,\n" +
      "           count(*) AS value,\n" +
      "           count(*) FILTER (WHERE msgs.half = 1) AS recent,\n" +
      "           count(*) FILTER (WHERE msgs.sentiment = 'POSITIVE') AS pos,\n" +
      "           count(*) FILTER (WHERE msgs.sentiment = 'NEGATIVE') AS neg,\n" +
      "           count(msgs.sentiment) AS rated\n" +
      "    FROM msgs, LATERAL regexp_matches(msgs.txt, '[\\U0001F1E6-\\U0001F1FF]{2}|[\\U0001F300-\\U0001F3FA\\U0001F400-\\U0001FAFF\\U00002600-\\U000027BF\\U00002B00-\\U00002BFF]', 'g') AS e\n" +
      "    GROUP BY 1\n" +
      "    ORDER BY 2 DESC\n" +
      "    LIMIT 50\n" +
      "),\n" +
      "tokens AS (\n" +
      "    SELECT name, value, recent, pos, neg, rated, 'keyword' AS kind FROM top_words\n" +
      "    UNION ALL\n" +
      "    SELECT name, value, recent, pos, neg, rated, 'emoji' AS kind FROM top_emojis\n" +
      ")\n" +
      "SELECT t.name,\n" +
      "       t.value,\n" +
      "       t.kind,\n" +
      "       -- share in the newer half against the older half: 0.4 = +40%. Rare tokens stay at 0.\n" +
      "       CASE WHEN t.value >= 10\n" +
      "            THEN round((((t.recent + 1.0) / s.newer) / ((t.value - t.recent + 1.0) / s.older) - 1)::numeric, 2)\n" +
      "            ELSE 0\n" +
      "       END AS trend,\n" +
      "       -- net sentiment from -1 to 1 over the rated mentions; empty when fewer than 10 are rated\n" +
      "       CASE WHEN t.rated >= 10\n" +
      "            THEN round((t.pos - t.neg)::numeric / t.rated, 2)\n" +
      "       END AS sentiment,\n" +
      "       s.total AS sample_size\n" +
      "FROM tokens t\n" +
      "CROSS JOIN sizes s\n" +
      "ORDER BY t.value DESC;",
  },
  {
    type: 'text',
    label: 'Text',
    description: 'Free-form markdown text block — headings, lists, links, and more',
    defaultConfig: {
      textConfig: {
        content: '## Title\n\nAdd your text here. You can use **bold**, *italic*, and [links](https://example.com).\n\n- Bullet point\n- Another point',
      },
    },
    sampleQuery: '--',
  },
];
